import type { ComponentProps } from 'react';

import type { TipTapVariable, TipTapDocument } from '../Input2/TipTapInput';
import type { ReminderAction } from '../ManualSelect/ManualSelectPure';
import { CampaignDayCardPure } from '../CampaignDayCard/CampaignDayCardPure';
import { CampaignNewDayPickerPure } from '../CampaignDayCard/CampaignNewDayPickerPure';
import { CampaignDayDelayPure } from '../CampaignDayCard/CampaignDayDelayPure';
import { CampaignDayDelayEditPure } from '../CampaignDayCard/CampaignDayDelayEditPure';
import { isStepDifferentFromTemplate } from '../CampaignDayCard/templateDiff';
import type { CampaignDayCardStep } from '../CampaignDayCard/stepDisplayInfo';
import { daysToMs, type EditStepPure, type CreateStepData } from './CGAddStepPure';
import type { StepType } from './CTSContentEditingPure';
import type { CadenceGraphNodesPure, CadenceStepTypePure, StepUpdateInputPure } from './types';
import type { SiftConfig } from './CTSContentEditingPure';

// ============================================================================
// Types
// ============================================================================

export type CGDelayStepPureProps = ComponentProps<'div'> & {
  first?: boolean;
  /**
   * The PREVIOUS day's delay node id. When set, the leading wait pill of
   * THIS day surfaces an inline "+ add new day" affordance whose
   * `afterStepId` is this delay node id — that routes through the
   * server's "add after a delay" branch, which inserts a brand-new
   * delay (= new day) between the previous day and this day with the
   * requested wait. Undefined for the first node.
   */
  prevDelayNodeId?: string;
  /**
   * URL to the cadence template page for this cadence (e.g.
   * `/templates/{templateId}`). Forwarded to `CampaignDayCardPure`
   * so its header surfaces a "Not in template" / "See in template"
   * link.
   */
  templateHref?: string;
  nodeId: string;
  nodes: CadenceGraphNodesPure;
  msUpToNow: number;
  hasSalesNav?: boolean | null;
  /**
   * When the contact is already a LinkedIn connection, set
   * `{ connect: '<reason>' }` so the picker disables the Connect option
   * with that explanation.
   */
  alreadyConnectedToContact?: boolean;
  editingStep?: EditStepPure;
  setEditingStep: (value: EditStepPure | undefined) => void;
  variables?: TipTapVariable[];
  documents?: TipTapDocument[];
  contactId?: string;
  onChangeType: (nodeId: string, type: string) => void;
  onDeleteNode: (nodeId: string) => void;
  onCreateStep: (data: CreateStepData) => unknown;
  onUpdateStep: (stepId: string, data: StepUpdateInputPure) => unknown;
  markFinished: (nodeId: string) => void;
  renderSiftConditionBuilder?: (props: {
    value: SiftConfig;
    onChange: (value: SiftConfig) => void;
  }) => React.ReactNode;
  // Reminder editor props (forwarded into the inline reminder editor).
  reminderActions?: ReminderAction[];
  reminderActionsLoading?: boolean;
  onCreateReminder?: (name: string) => Promise<void>;
  reminderCreating?: boolean;
  onReminderError?: (message: string) => void;
  // Render prop for recursive CGStep rendering
  renderCGStep: (props: {
    nodeId: string;
    first?: boolean;
    msUpToNow: number;
    prevDelayNodeId?: string;
  }) => React.ReactNode;
  /** When true, voice_note step type is available in the picker. */
  voiceEnabled?: boolean;
  /** When true, the call step type is available (team voice-calling flag). */
  voiceCallingEnabled?: boolean;
  /**
   * Display-only mode — hides every edit affordance (delay edit pill,
   * new-day pickers, add-step footers, row click-to-edit). Used by hosts
   * that can render the step plan but don't wire the step mutations
   * (e.g. the extension sidepanel).
   */
  readOnly?: boolean;
};

// ============================================================================
// Helper
// ============================================================================

function msToDays(ms: number): number {
  return Math.round(ms / (1000 * 60 * 60 * 24));
}


// ============================================================================
// CGDelayStepPure Component
// ============================================================================

export function CGDelayStepPure({
  nodeId,
  nodes,
  first: _first,
  prevDelayNodeId,
  templateHref,
  hasSalesNav: _hasSalesNav,
  alreadyConnectedToContact,
  msUpToNow,
  onChangeType: _onChangeType,
  onDeleteNode: _onDeleteNode,
  editingStep,
  setEditingStep,
  onCreateStep,
  onUpdateStep,
  variables,
  documents,
  markFinished,
  contactId: _contactId,
  renderCGStep,
  renderSiftConditionBuilder,
  reminderActions,
  reminderActionsLoading,
  onCreateReminder,
  reminderCreating,
  onReminderError,
  voiceEnabled,
  voiceCallingEnabled,
  readOnly,
}: CGDelayStepPureProps) {
  const node = nodes[nodeId];
  if (node == null) return null;

  const day = msToDays(node.delay);

  return (
    <>
      {node.state !== 'finished' && (
        <>
          {(() => {
            // Read-only hosts get the bare wait band — no edit pill, no
            // insert-before picker. Editing state can't be entered in
            // this mode so none of the branches below apply.
            if (readOnly) {
              return (
                <CampaignDayDelayPure
                  days={day}
                  isDifferentFromTemplate={
                    node.templateStepId != null && node.templateStep?.delay !== node.delay
                  }
                  className="mx-2"
                />
              );
            }
            // The "insert before this card" picker opens when add-mode is
            // targeting prevDelayNodeId — the slot ABOVE this card. When that
            // happens we swap the wait band for the picker card so the
            // picker visually slots where the wait band was.
            const isInsertingBefore =
              prevDelayNodeId != null &&
              editingStep?.type === 'add' &&
              editingStep.afterStepId === prevDelayNodeId &&
              editingStep.intent === 'new-day';
            const isPickingBefore = isInsertingBefore && editingStep.data == null;
            const addingBefore = isInsertingBefore && editingStep.data != null
              ? (editingStep.data.type as CadenceStepTypePure)
              : undefined;

            if (editingStep?.type === 'edit' && editingStep.stepId === node.id) {
              return (
                <CampaignDayDelayEditPure
                  initialDays={day}
                  isDifferentFromTemplate={
                    node.templateStepId != null && node.templateStep?.delay !== node.delay
                  }
                  className="mx-2"
                  onSubmit={async (days) => {
                    await onUpdateStep(node.id, { delay: daysToMs(days) });
                    setEditingStep(undefined);
                  }}
                  onCancel={() => setEditingStep(undefined)}
                />
              );
            }

            if (isInsertingBefore && prevDelayNodeId != null) {
              const NEW_DAY_DEFAULT_MS = daysToMs(2);
              // Render BOTH the picker card AND the existing wait pill
              // below it. The picker represents the new draft day being
              // inserted between the previous card and this card; the
              // wait pill still describes THIS card's delay (it stays
              // applicable after insertion: prev → new draft → wait → this).
              return (
                <>
                  <div className="mx-2">
                    <CampaignNewDayPickerPure
                      defaultDelayDays={2}
                    voiceEnabled={voiceEnabled}
                    voiceCallingEnabled={voiceCallingEnabled}
                    isPickingStepType={isPickingBefore}
                    addingStepType={addingBefore}
                    existingTypes={[]}
                    disabledStepReasons={
                      alreadyConnectedToContact
                        ? { connect: 'You are already connected to this contact.' }
                        : undefined
                    }
                    variables={variables}
                    documents={documents}
                    renderSiftConditionBuilder={renderSiftConditionBuilder}
                    reminderActions={reminderActions}
                    reminderActionsLoading={reminderActionsLoading}
                    onCreateReminder={onCreateReminder}
                    reminderCreating={reminderCreating}
                    onReminderError={onReminderError}
                    onAddStep={() =>
                      setEditingStep({
                        type: 'add',
                        option: 'one',
                        intent: 'new-day',
                        afterStepId: prevDelayNodeId,
                      })
                    }
                    onCancelPickStepType={() => setEditingStep(undefined)}
                    onPickStepType={(type) => {
                      if (type === 'follow' || type === 'likeRecentPosts' || type === 'visit') {
                        void onCreateStep({
                          type: type as StepType,
                          delay: NEW_DAY_DEFAULT_MS,
                          afterStepId: prevDelayNodeId,
                          ...(type === 'likeRecentPosts' && { postsToLike: 1 }),
                        });
                        setEditingStep(undefined);
                        return;
                      }
                      setEditingStep({
                        type: 'add',
                        option: 'one',
                        intent: 'new-day',
                        afterStepId: prevDelayNodeId,
                        data: { id: 'pending', type: type as StepType, delay: NEW_DAY_DEFAULT_MS },
                      });
                    }}
                    onSaveNewStep={async (type, data) => {
                      const create: CreateStepData = {
                        type: type as StepType,
                        delay: NEW_DAY_DEFAULT_MS,
                        afterStepId: prevDelayNodeId,
                      };
                      if (typeof data.subject === 'string') create.subject = data.subject;
                      if (typeof data.body === 'string') create.body2 = data.body;
                      if (typeof data.postsToLike === 'number') create.postsToLike = data.postsToLike;
                      if (typeof data.cc === 'string') create.cc = data.cc;
                      if (typeof data.bcc === 'string') create.bcc = data.bcc;
                      if (data.siftConditions != null) create.siftConditions = data.siftConditions;
                      if (type === 'inmail') create.onlyOpenLink = data.onlyOpenLink ?? false;
                      await onCreateStep(create);
                      setEditingStep(undefined);
                    }}
                    onCancelAddStep={() => {
                      setEditingStep({
                        type: 'add',
                        option: 'one',
                        intent: 'new-day',
                        afterStepId: prevDelayNodeId,
                      });
                    }}
                  />
                  </div>
                  <CampaignDayDelayPure
                    days={day}
                    isDifferentFromTemplate={
                      node.templateStepId != null && node.templateStep?.delay !== node.delay
                    }
                    onEdit={() =>
                      setEditingStep({ type: 'edit', option: 'one', stepId: node.id })
                    }
                    className="mx-2"
                  />
                </>
              );
            }

            return (
              <CampaignDayDelayPure
                days={day}
                isDifferentFromTemplate={
                  node.templateStepId != null && node.templateStep?.delay !== node.delay
                }
                onEdit={() => setEditingStep({ type: 'edit', option: 'one', stepId: node.id })}
                onAddNewDay={
                  prevDelayNodeId != null
                    ? () =>
                        setEditingStep({
                          type: 'add',
                          option: 'one',
                          intent: 'new-day',
                          afterStepId: prevDelayNodeId,
                        })
                    : undefined
                }
                className="mx-2"
              />
            );
          })()}
          {(() => {
            const groupSet = new Set(node.group);
            const lastInGroup = node.group[node.group.length - 1];
            // Types that get the inline ADD editor when picked from the
            // new picker. Action-only types (follow / likeRecentPosts /
            // visit) use immediate-create from the picker — they never
            // reach the editor.
            const addTypeUsesInlineEditor = (t: string | undefined): boolean =>
              t === 'connect' ||
              t === 'message' ||
              t === 'inmail' ||
              t === 'email' ||
              t === 'reminder' ||
              t === 'sift' ||
              t === 'voice_note';
            // Hide steps that have already resolved (finished / skipped)
            // OR will never run for this contact (canceled — typically
            // connect when already-connected; contact_unavailable when
            // the contact has no LinkedIn / no email). Showing them
            // would mislead the user into thinking the cadence will
            // execute them.
            const steps: CampaignDayCardStep[] = node.group
              .map((id) => nodes[id])
              .filter(
                (n): n is NonNullable<typeof n> =>
                  n != null &&
                  n.state !== 'finished' &&
                  n.state !== 'skipped' &&
                  n.state !== 'canceled' &&
                  n.state !== 'contact_unavailable',
              )
              .map((n) => {
                const error =
                  n.errorMessage ??
                  n.computedError?.default ??
                  n.computedError?.subject ??
                  n.computedError?.body2 ??
                  null;
                return {
                  id: n.id,
                  type: n.type,
                  state: n.state,
                  body2: n.body2,
                  subject: n.subject,
                  postsToLike: n.postsToLike,
                  cc: n.cc,
                  bcc: n.bcc,
                  siftConditions: n.siftConditions,
                  onlyOpenLink: n.onlyOpenLink,
                  isDifferentFromTemplate: isStepDifferentFromTemplate(n),
                  error,
                  skipReason: n.skipReason,
                };
              });

            // The whole day card is "different" when the delay node itself
            // was added by the user (no template link) or its delay was edited
            // away from the template's delay.
            const cardIsDifferent =
              node.templateStepId == null ||
              (node.templateStep != null && node.templateStep.delay !== node.delay);

            // The in-group picker opens only for "in-group" intent — a
            // "new-day" intent shares the same afterStepId (this day's
            // lastInGroup) but is owned by the next day's leading wait
            // band, so we must NOT also open the in-card picker here.
            const isInGroupAdd =
              editingStep?.type === 'add' &&
              editingStep.afterStepId === lastInGroup &&
              editingStep.intent !== 'new-day';
            const isPickingStepType = isInGroupAdd && editingStep.data == null;
            const addingStepType =
              isInGroupAdd &&
              editingStep.data != null &&
              addTypeUsesInlineEditor(editingStep.data.type)
                ? (editingStep.data.type as CadenceStepTypePure)
                : undefined;

            return (
              <div className="mx-2">
                <CampaignDayCardPure
                  date={new Date(msUpToNow + node.delay)}
                  now={undefined}
                  steps={steps}
                  isDifferentFromTemplate={cardIsDifferent}
                  templateHref={templateHref}
                  voiceEnabled={voiceEnabled}
                  voiceCallingEnabled={voiceCallingEnabled}
                  readOnly={readOnly}
                  isPickingStepType={isPickingStepType}
                  addingStepType={addingStepType}
                  disabledStepReasons={
                    alreadyConnectedToContact
                      ? { connect: 'You are already connected to this contact.' }
                      : undefined
                  }
                  editingStepId={
                    editingStep?.type === 'edit' && groupSet.has(editingStep.stepId)
                      ? editingStep.stepId
                      : undefined
                  }
                  variables={variables}
                  documents={documents}
                  renderSiftConditionBuilder={renderSiftConditionBuilder ?? (() => null)}
                  reminderActions={reminderActions}
                  reminderActionsLoading={reminderActionsLoading}
                  onCreateReminder={onCreateReminder ?? (async () => {})}
                  reminderCreating={reminderCreating}
                  onReminderError={onReminderError ?? (() => {})}
                  onEditStep={(stepId) => setEditingStep({ type: 'edit', option: 'one', stepId })}
                  onDeleteStep={_onDeleteNode}
                  onMarkStepFinished={markFinished}
                  onSaveStep={async (stepId, data) => {
                    // The update mutation's schema accepts `string | undefined`
                    // (optional) but rejects null — passing null triggers
                    // "Expected string, received null". Only include fields
                    // the editor actually populated, and treat null/empty as
                    // "no change" by omitting the key entirely.
                    const update: StepUpdateInputPure = {};
                    if (typeof data.subject === 'string') update.subject = data.subject;
                    if (typeof data.body === 'string') update.body2 = data.body;
                    if (typeof data.postsToLike === 'number') update.postsToLike = data.postsToLike;
                    if (typeof data.cc === 'string') update.cc = data.cc;
                    if (typeof data.bcc === 'string') update.bcc = data.bcc;
                    if (data.siftConditions != null) update.siftConditions = data.siftConditions;
                    if (typeof data.onlyOpenLink === 'boolean') update.onlyOpenLink = data.onlyOpenLink;
                    await onUpdateStep(stepId, update);
                    setEditingStep(undefined);
                  }}
                  onCancelEditStep={() => setEditingStep(undefined)}
                  onAddStep={() =>
                    setEditingStep({ type: 'add', option: 'one', afterStepId: lastInGroup })
                  }
                  onPickStepType={(type) => {
                    // Action-only types (no body / subject to author) are
                    // created immediately when picked. Content types (message,
                    // connect, inmail, email, reminder) stash `data` on
                    // editingStep so the inline ADD editor renders below.
                    if (type === 'follow' || type === 'likeRecentPosts' || type === 'visit') {
                      void onCreateStep({
                        type: type as StepType,
                        delay: 0,
                        afterStepId: lastInGroup,
                        ...(type === 'likeRecentPosts' && { postsToLike: 1 }),
                      });
                      setEditingStep(undefined);
                      return;
                    }
                    setEditingStep({
                      type: 'add',
                      option: 'one',
                      afterStepId: lastInGroup,
                      data: { id: 'pending', type: type as StepType, delay: 0 },
                    });
                  }}
                  onCancelPickStepType={() => setEditingStep(undefined)}
                  onSaveNewStep={async (type, data) => {
                    // Mirror the edit-save sparse payload — only include
                    // fields the editor actually populated. Avoids `null`
                    // rejections from the create mutation's Zod schema.
                    const create: CreateStepData = {
                      type: type as StepType,
                      delay: 0,
                      afterStepId: lastInGroup,
                    };
                    if (typeof data.subject === 'string') create.subject = data.subject;
                    if (typeof data.body === 'string') create.body2 = data.body;
                    if (typeof data.postsToLike === 'number') create.postsToLike = data.postsToLike;
                    if (typeof data.cc === 'string') create.cc = data.cc;
                    if (typeof data.bcc === 'string') create.bcc = data.bcc;
                    if (data.siftConditions != null) create.siftConditions = data.siftConditions;
                    // InMail's "only open link" flag isn't surfaced in the
                    // inline editor yet — default to `false` so the created
                    // step has a deterministic value rather than relying on
                    // server-side / db column defaults.
                    if (type === 'inmail') create.onlyOpenLink = data.onlyOpenLink ?? false;
                    await onCreateStep(create);
                    setEditingStep(undefined);
                  }}
                  onCancelAddStep={() => {
                    // Go back to the picker (data cleared) rather than
                    // closing the whole add flow — lets the user reconsider
                    // their step-type choice without re-clicking "add step".
                    setEditingStep({ type: 'add', option: 'one', afterStepId: lastInGroup });
                  }}
                />
              </div>
            );
          })()}
          {/*
            Trailing "add a step on a new day" only renders at the END of
            the chain. Between two existing days the next delay node's wait
            pill is the visual connector — putting the trigger here too
            would interrupt the flow and disconnect the wait band from the
            cards on either side (see screenshot regression).
          */}
          {!readOnly && node.next.one == null && (
            <span
              aria-hidden
              data-testid="cadence-terminal-add-spine"
              // Negative bottom margin closes the gap created by the
              // trailing trigger's `my-3` so the spine reaches flush to
              // the button below it.
              className="mx-auto -mb-3 block h-6 w-px bg-lynch-200 dark:bg-slate-700"
            />
          )}
          {!readOnly && node.next.one == null && (() => {
            const NEW_DAY_DEFAULT_MS = daysToMs(2);
            const isHere =
              editingStep?.type === 'add' &&
              editingStep.afterStepId === node.id &&
              editingStep.intent === 'new-day';
            const isPickingHere = isHere && editingStep.data == null;
            const addingHere = isHere && editingStep.data != null
              ? (editingStep.data.type as CadenceStepTypePure)
              : undefined;

            return (
              <div className="mx-2">
                <CampaignNewDayPickerPure
                  defaultDelayDays={2}
                  voiceEnabled={voiceEnabled}
                  voiceCallingEnabled={voiceCallingEnabled}
                  // Terminal picker — there's no card below to spine
                  // down to. The card above is the last day card.
                  hasNeighborAbove
                  hasNeighborBelow={false}
                  isPickingStepType={isPickingHere}
                  addingStepType={addingHere}
                  // Trailing picker has no upstream constraints — we're
                  // starting a brand-new day. Once-only step rules are
                  // enforced per-day, not across the whole cadence.
                  existingTypes={[]}
                  disabledStepReasons={
                    alreadyConnectedToContact
                      ? { connect: 'You are already connected to this contact.' }
                      : undefined
                  }
                  variables={variables}
                  documents={documents}
                  renderSiftConditionBuilder={renderSiftConditionBuilder ?? (() => null)}
                  reminderActions={reminderActions}
                  reminderActionsLoading={reminderActionsLoading}
                  onCreateReminder={onCreateReminder ?? (async () => {})}
                  reminderCreating={reminderCreating}
                  onReminderError={onReminderError ?? (() => {})}
                  onAddStep={() =>
                    setEditingStep({
                      type: 'add',
                      option: 'one',
                      intent: 'new-day',
                      afterStepId: node.id,
                    })
                  }
                  onCancelPickStepType={() => setEditingStep(undefined)}
                  onPickStepType={(type) => {
                    if (type === 'follow' || type === 'likeRecentPosts' || type === 'visit') {
                      void onCreateStep({
                        type: type as StepType,
                        delay: NEW_DAY_DEFAULT_MS,
                        afterStepId: node.id,
                        ...(type === 'likeRecentPosts' && { postsToLike: 1 }),
                      });
                      setEditingStep(undefined);
                      return;
                    }
                    setEditingStep({
                      type: 'add',
                      option: 'one',
                      intent: 'new-day',
                      afterStepId: node.id,
                      data: { id: 'pending', type: type as StepType, delay: NEW_DAY_DEFAULT_MS },
                    });
                  }}
                  onSaveNewStep={async (type, data) => {
                    const create: CreateStepData = {
                      type: type as StepType,
                      delay: NEW_DAY_DEFAULT_MS,
                      afterStepId: node.id,
                    };
                    if (typeof data.subject === 'string') create.subject = data.subject;
                    if (typeof data.body === 'string') create.body2 = data.body;
                    if (typeof data.postsToLike === 'number') create.postsToLike = data.postsToLike;
                    if (typeof data.cc === 'string') create.cc = data.cc;
                    if (typeof data.bcc === 'string') create.bcc = data.bcc;
                    if (data.siftConditions != null) create.siftConditions = data.siftConditions;
                    // InMail's "only open link" flag isn't surfaced in the
                    // inline editor yet — default to `false` so the created
                    // step has a deterministic value rather than relying on
                    // server-side / db column defaults.
                    if (type === 'inmail') create.onlyOpenLink = data.onlyOpenLink ?? false;
                    await onCreateStep(create);
                    setEditingStep(undefined);
                  }}
                  onCancelAddStep={() => {
                    // Return to the picker (drop the chosen type) so the user
                    // can reconsider without re-clicking the trigger button.
                    setEditingStep({
                      type: 'add',
                      option: 'one',
                      intent: 'new-day',
                      afterStepId: node.id,
                    });
                  }}
                />
              </div>
            );
          })()}
        </>
      )}

      {node.next.one && renderCGStep({
        nodeId: node.next.one,
        msUpToNow: msUpToNow + node.delay,
        // Hand the next delay node the id of THIS delay node so its
        // leading "+ add new day" trigger sends `afterStepId = our
        // delay id`. That routes through the server's "add after a
        // delay" branch, which inserts a fresh delay (= new day)
        // between us and the next day with the requested wait.
        prevDelayNodeId: node.id,
      })}
    </>
  );
}
